import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { CatalogCategory, CatalogProduct, CatalogResponse, CatalogTag, LastOrder, StoreInfo } from '../types/catalog';
import { useAppMetaStore } from './appMeta';
import { useClientAuthStore } from './auth';
import { apiUrl } from '../lib/apiBase';

export const useCatalogStore = defineStore('client-catalog', () => {
    const categories = ref<CatalogCategory[]>([]);
    const storeInfo = ref<StoreInfo | null>(null);
    const availableTags = ref<CatalogTag[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const activeOrders = ref<LastOrder[]>([]);
    const lastOrderLoading = ref(false);

    const search = ref('');
    const activeCategoryId = ref<string | null>(null);
    const activeTagSlug = ref<string | null>(null);

    /** Disponíveis primeiro, esgotados no fim — mesma ordem relativa dentro de cada grupo. */
    function sortProductsAvailFirst(products: CatalogProduct[]): CatalogProduct[] {
        return [...products].sort((a, b) => {
            const ax = a.is_available ? 0 : 1;
            const bx = b.is_available ? 0 : 1;
            return ax - bx;
        });
    }

    const filtered = computed(() => {
        let cats = categories.value;

        if (activeCategoryId.value) {
            cats = cats.filter(c => c.id === activeCategoryId.value);
        }

        // Filtro por tag — transversal a categorias
        if (activeTagSlug.value) {
            const slug = activeTagSlug.value;
            cats = cats
                .map(c => ({
                    ...c,
                    products: c.products.filter(p => p.tags?.some(t => t.slug === slug) ?? false),
                }))
                .filter(c => c.products.length > 0);
        }

        const q = search.value.trim().toLowerCase();
        if (q) {
            cats = cats
                .map(c => ({
                    ...c,
                    products: sortProductsAvailFirst(
                        c.products.filter(p => {
                            const dn = (p.description ?? '').toLowerCase();
                            return p.name.toLowerCase().includes(q) || dn.includes(q);
                        }),
                    ),
                }))
                .filter(c => c.products.length > 0);
        } else {
            cats = cats.map(c => ({
                ...c,
                products: sortProductsAvailFirst(c.products),
            }));
        }

        return cats;
    });

    /** Destaques só para cliente autenticado (consumido no composable da página). */
    const featured = computed<CatalogProduct[]>(() =>
        categories.value.flatMap(c => c.products).filter(p => p.is_featured && p.is_available),
    );

    async function fetchCatalog(): Promise<void> {
        const meta = useAppMetaStore();
        if (!meta.tenantId) {
            error.value = 'Loja não identificada. Abra pelo link da loja ou configure o tenant de demonstração.';
            return;
        }

        loading.value = true;
        error.value = null;
        try {
            const url = apiUrl('catalog');
            const res = await fetch(url, {
                headers: {
                    Accept: 'application/json',
                    'X-Tenant-ID': meta.tenantId,
                },
            });

            if (res.status === 422) {
                throw new Error('Tenant inválido');
            }
            if (!res.ok) {
                throw new Error('Catálogo indisponível');
            }

            const data = (await res.json()) as CatalogResponse;
            categories.value = data.categories ?? [];
            storeInfo.value = data.store ?? null;
            availableTags.value = data.available_tags ?? [];
        } catch {
            categories.value = [];
            storeInfo.value = null;
            error.value = 'Não foi possível carregar o catálogo.';
        } finally {
            loading.value = false;
        }
    }

    async function fetchLastOrder(): Promise<void> {
        const auth = useClientAuthStore();
        if (!auth.isAuthenticated || !auth.token) {
            return;
        }

        const meta = useAppMetaStore();
        lastOrderLoading.value = true;

        try {
            const res = await fetch(apiUrl('orders/last'), {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${auth.token}`,
                    ...(meta.tenantId ? { 'X-Tenant-ID': meta.tenantId } : {}),
                },
            });
            if (!res.ok) {
                return;
            }
            const data = (await res.json()) as { orders: LastOrder[] };
            activeOrders.value = data.orders ?? [];
        } catch {
            activeOrders.value = [];
        } finally {
            lastOrderLoading.value = false;
        }
    }

    return {
        categories,
        storeInfo,
        availableTags,
        loading,
        error,
        activeOrders,
        lastOrderLoading,
        search,
        activeCategoryId,
        activeTagSlug,
        filtered,
        featured,
        fetchCatalog,
        fetchLastOrder,
    };
});
